import type { GlobalMetricsResponse } from '../../../../utils/globalMetrics';

/**
 * Format a numeric stat for display in a hero stat card.
 *
 * Returns the supplied `fallback` (the existing hard-coded design value) when
 * the API field is `null` or `undefined`. Otherwise returns the value formatted
 * with `Intl.NumberFormat` using the active intl locale, so e.g. an English
 * locale renders `2424000` as `"2,424,000"`.
 */
export const formatStatValue = (
	value: number | null | undefined,
	locale: string,
	fallback: string,
): string => {
	if (value === null || value === undefined || Number.isNaN(value)) {
		return fallback;
	}
	return new Intl.NumberFormat(locale).format(value);
};

/**
 * Placeholder string shown in any stat card whenever the API response is
 * missing or a specific field is `null`. Kept as a single shared constant so
 * the three API-backed cards stay visually consistent during error / loading-
 * edge states.
 */
const NOT_AVAILABLE = 'N/A';

/**
 * Hard-coded marketing figure for the "Customers using" card. The `/global`
 * payload has no field for tenant / customer count, so this card always
 * renders the static value until backend exposes a `customerCount`-equivalent.
 */
const CUSTOMERS_USING_HARDCODED = '350K +';

export const STAT_FALLBACKS: {
	readonly entitiesIndexed: 'N/A';
	readonly relationshipsCreated: 'N/A';
	readonly queriesThisWeek: 'N/A';
	readonly customersUsing: '350K +';
} = {
	entitiesIndexed: NOT_AVAILABLE,
	relationshipsCreated: NOT_AVAILABLE,
	queriesThisWeek: NOT_AVAILABLE,
	customersUsing: CUSTOMERS_USING_HARDCODED,
} as const;

/**
 * Map the `/global` API payload to the four formatted display strings used by
 * the stat cards. `customersUsing` has no field in the global payload and
 * stays hard-coded.
 *
 * - `entitiesIndexed`      ⟵ `estimatedNodesNow` (preferred) ⟶ `totalNodes`
 * - `relationshipsCreated` ⟵ `totalRelationships`
 * - `queriesThisWeek`      ⟵ `avgFetchQueriesPerDay × 7`
 * - `customersUsing`       ⟵ static `STAT_FALLBACKS.customersUsing`
 */
export const selectStatValues = (
	metrics: GlobalMetricsResponse | null,
	locale: string,
): {
	entitiesIndexed: string;
	relationshipsCreated: string;
	queriesThisWeek: string;
	customersUsing: string;
} => {
	const entitiesValue = metrics?.estimatedNodesNow ?? metrics?.totalNodes ?? null;
	const queriesValue =
		typeof metrics?.avgFetchQueriesPerDay === 'number' ? metrics.avgFetchQueriesPerDay * 7 : null;

	return {
		entitiesIndexed: formatStatValue(entitiesValue, locale, STAT_FALLBACKS.entitiesIndexed),
		relationshipsCreated: formatStatValue(
			metrics?.totalRelationships,
			locale,
			STAT_FALLBACKS.relationshipsCreated,
		),
		queriesThisWeek: formatStatValue(queriesValue, locale, STAT_FALLBACKS.queriesThisWeek),
		customersUsing: STAT_FALLBACKS.customersUsing,
	};
};
